import React, { useEffect, useState } from "react";
import { Box, Text, Button, Notification, Heading, Anchor } from "grommet";
import axios from "axios";
import API_URL from "../../config/config"; // adjust import as needed
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { StatusWarning } from "grommet-icons";
import { Spinner } from 'grommet';

const HandleInviteConfirmation = ({ managerName, id }) => {
  const navigate = useNavigate();
  const { manager_id } = useParams();
  const [inviteStatus, setInviteStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // State to display a success message
  const [showSuccess, setShowSuccess] = useState(false);
  const [managerWarning, setManagerWarning] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [searchParams] = useSearchParams();
  const inviteStatusFromEmail = searchParams.get("invite_status");
  const [isAutoRedirecting, setIsAutoRedirecting] = useState(false);

  const check_if_manager = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/user/check_if_user_is_manager/${manager_id}/${id}`,
      );
      const isManagerFlag = response.data.isManagerFlag;
      console.log("Response:", response.data.isManagerFlag);
      if (isManagerFlag === 1) {
        console.log("User is a manager");
        setManagerWarning(true);
      }
    } catch (error) {
      console.error("Error checking if user is a manager:", error);
      setError(
        error.response?.data?.message || error.message || "An error occurred.",
      );
    }
  };

  useEffect(() => {
    if (inviteStatusFromEmail === "1") {
      setIsAutoRedirecting(true);
      handleAcceptInvite();
    } else if (inviteStatusFromEmail === "2") {
      setIsAutoRedirecting(true);
      handleRejectInvite();
    }
  }, [inviteStatusFromEmail]);

  useEffect(() => {
    check_if_manager();
    // Additional logic if needed
  }, []);

  // When `inviteStatus` is set to "accepted" (1), show the success notification,
  // then navigate after 2 seconds
  useEffect(() => {
    if (inviteStatus === 1) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        navigate("/");
      }, 2000);
      return () => clearTimeout(timer);
    } else if (inviteStatus === 2) {
      setShowRejected(true);
      const timer = setTimeout(() => {
        navigate("/");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [inviteStatus, navigate]);

  // Handler for accept invite (status=1)
  const handleAcceptInvite = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/user/update_invite_status`,
        {
          manager_id,
          uuid: id,
          invite_status: 1,
        },
      );
      console.log("Invite accepted:", response.data);
      setInviteStatus(1);
      setIsLoading(false);
    } catch (error) {
      console.error("Error accepting invite:", error);
      setError(
        error.response?.data?.message || error.message || "An error occurred.",
      );
      setIsLoading(false);
    }
  };

  // Handler for reject invite (status=2)
  const handleRejectInvite = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/user/update_invite_status`,
        {
          manager_id,
          uuid: id,
          invite_status: 2,
        },
      );
      console.log("Invite rejected:", response.data);
      setInviteStatus(2);
      setIsLoading(false);
    } catch (error) {
      setError(
        error.response?.data?.message || error.message || "An error occurred.",
      );
      console.error("Error rejecting invite:", error);
      setIsLoading(false);
    }
  };

  return (
    <Box pad="medium" gap="small">
      <Heading level={1}>
        You are invited! Welcome to the HPE Digital Skill Advisor.
      </Heading>
      {managerName != "unknown" ? (
        <Text size="large" margin={{ bottom: "medium" }}>
          Your manager {managerName} has invited you to join the HPE Digital Skill
          Advisor. Please click a button below to respond to the invite.
        </Text>
      ) : (
        <Text size="large" margin={{ bottom: "medium" }}>
          You have been invited to join HPE Digital Skill Advisor. Please click a
          button below to respond to the invite.
        </Text>
      )}

      {managerWarning && (
        <Box
          pad="medium"
          // background="status-warning"
          border={{ color: "status-warning", size: "small" }}
          round="small"
          elevation="medium"
          margin={{ vertical: "small" }}
          direction="column"
        >
          <Box direction="row" align="center" gap="small" alignContent="center">
            <StatusWarning color="brand" size="medium" />
            <Text size="medium" weight="bold" color="brand">
              Warning: Changes to Your Managerial Rights
            </Text>
          </Box>
          <Box>
            <Text size="medium">
              By accepting this invitation, your managerial rights will be
              changed, and you will lose access to your current team and all
              associated team assessments. This action is irreversible, and any
              existing team data under your management will no longer be
              available.
            </Text>
          </Box>
        </Box>
      )}

      {/* Buttons for Accepting / Rejecting Invite */}
      {!isAutoRedirecting && (
        <Box direction="row" gap="medium">
          <Button
            label="Accept Invite"
            primary
            onClick={handleAcceptInvite}
            disabled={isLoading}
          />
          <Button
            label="Reject Invite"
            onClick={handleRejectInvite}
            disabled={isLoading}
            color="status-critical"
          />
        </Box>
      )}
      {/* Conditional error rendering */}
      {error && error.includes("The activation link has already been used") ? (
        <Box
          direction="row"
          gap="small"
          align="center"
          margin={{ top: "small" }}
        >
          <Text color="status-critical">
            The activation link has already been used. Please proceed to the
            application and sign in using your existing credentials or create a
            new account.
          </Text>
          <Anchor
            label="Login here"
            href="https://sga.education.hpe.com/login"
            target="_blank"
            rel="noopener noreferrer"
            color="status-critical"
          />
        </Box>
      ) : (
        error && <Text color="status-critical">{error}</Text>
      )}

      {/* SUCCESS NOTIFICATION */}
      {showSuccess && (
        <Notification
          status="normal"
          message="Success! Redirecting you to the application..."

        // onClose={() => setShowSuccess(false)}
        />
      )}
      {showRejected && (
        <Notification
          status="critical"
          message="Invite rejected. Redirecting you to the application..."
        // If you want a close button to cancel the notification, you can add:
        // onClose={() => setShowSuccess(false)}
        />
      )}
    </Box>
  );
};

export default HandleInviteConfirmation;
